import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';

import { apiSahClaims } from '~constants/api';
import { DOCUMENT_TYPES, PAGE_SIZE, VALIDATION_POLL_INTERVAL_MS } from '~constants/common';
import { getMissingContent } from '~constants/notFoundMap';

import { getErrorMessage } from '~libs/axiosWrapper';
import { getStreamableZipDownload } from '~libs/downloadUtils';
import { download } from '~libs/genericAction';
import { useSearch } from '~libs/searchParams';
import { useToast } from '~libs/toast';

import {
  Button,
  ClickableIcon,
  ContainerHeader,
  DownloadButton,
  ErrorResponse,
  Modal,
  PageNotFound,
  Spinner,
  Toolbar,
} from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faArrowLeft, faMagnifyingGlass, faTrash } from '~components/icon';
import { useDocumentBatchGeneration } from '~main/components/document-batch-jobs/useDocumentBatchGeneration';

import { getAll as getServices } from '~appointments/actions/serviceTypes';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { useGetSahServicesQuery } from '~lookups/actions/sahServices';
import searchFields from '~support-at-home/forms/claims/claimItemsSearch';
import {
  CLAIM_STATUS_FINALISED,
  isClaimClaimed,
  isClaimLocked,
  isClaimSubmitted,
  isSaInvoiceHeld,
} from '~support-at-home/lib/claimStatus';
import { parseSahApiError } from '~support-at-home/lib/sahApiError';
import { useCanAdministerClaims } from '~support-at-home/hooks/useCanAdministerClaims';
import { ClaimApiPushResult } from '~support-at-home/models';

import { setTitle } from '~main/actions/login';
import {
  claimsApi,
  useDeleteClaimMutation,
  useFinaliseAndSubmitClaimMutation,
  useFinaliseClaimMutation,
  useGetClaimItemsQuery,
  useGetClaimQuery,
  useSendClaimToSaMutation,
  useSubmitClaimMutation,
  useUnfinaliseClaimMutation,
} from '~support-at-home/actions/claims';

import ClaimApiPushSummary from './ClaimApiPushSummary';
import ClaimDetailsEdit from './ClaimDetailsEdit';

const Claim: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedPage, setDebouncedPage] = useState(1);
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  // Result of the most recent reconcile. Held here rather than read from the claim because it
  // describes an ACTION ("4 updated, 1 removed", plus any warnings) rather than the claim's state -
  // there is nothing persisted for it to come from, and it should not survive a reload.
  const [lastSync, setLastSync] = useState<ClaimApiPushResult | null>(null);
  const [searchParams, setSearchParams] = useSearch('claim-items-search', {});
  const search = (values: { [key: string]: any }) => setSearchParams(values);
  const pageDebounceTimer = useRef<ReturnType<typeof setTimeout>>();

  /**
   * Toggles one of the push-summary banner's drill-down filters (TAV-2591 `show_attachment_required`,
   * TAV-2592 `show_late_submission`).
   *
   * searchParams is spread straight into the items query (itemsQueryArgs below), so setting a key
   * filters the paginated table server-side - which is what makes "View items" work across pages,
   * unlike an in-page anchor.
   *
   * The filters are ALTERNATIVES, not composable facets: every key here lands in a single `where`
   * object on the API side (company_sah_claim_items.mjs), so leaving a sibling set ANDs them and
   * "show me the flagged rows" returns only the rows flagged BOTH ways - usually none, while both
   * links read "Show all items". Turning one on therefore clears the others.
   *
   * Both page setters, not just the visible one: itemsQueryArgs reads `searchParams` (immediate) and
   * `debouncedPage` (150ms behind), so setting only currentPage fires one request for the NEW filter
   * at the OLD offset - an empty table for 150ms, which is the worst possible feedback for a button
   * whose whole job is "show me the flagged rows".
   */
  const toggleItemsFilter = (key: 'show_attachment_required' | 'show_late_submission') => {
    const wasActive = searchParams[key] === 'Yes';
    const { show_attachment_required, show_late_submission, ...rest } = searchParams;
    search(wasActive ? rest : { ...rest, [key]: 'Yes' });
    setCurrentPage(1);
    setDebouncedPage(1);
  };

  const dispatch = useAppDispatch();

  const { generateSeparate, isGenerating } = useDocumentBatchGeneration();
  const { showToast } = useToast();
  const { data: sahServicesData } = useGetSahServicesQuery();
  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);
  const claimItemsSearchFields = useMemo(
    () => searchFields({ sahServices: sahServicesData ?? [], serviceTypes: serviceTypes ?? [] }),
    [sahServicesData, serviceTypes],
  );

  const [deleteClaim, { isLoading: isDeleting, error: deleteError, reset: resetDeleteClaim }] =
    useDeleteClaimMutation();
  const [finaliseClaim, { isLoading: finaliseLoading, error: finaliseError, reset: resetFinaliseClaim }] =
    useFinaliseClaimMutation();
  const [unfinaliseClaim, { isLoading: unfinaliseLoading, reset: resetUnfinaliseClaim }] =
    useUnfinaliseClaimMutation();
  // No `error` pulled off the vendor-calling mutations: their failures surface through their own
  // toast, not the shared error banner - see the `errors` array below. `reset` is still needed
  // because RTK Query keeps the rejection in hook state regardless of whether anything reads it.
  const [submitClaim, { isLoading: submitLoading, reset: resetSubmitClaim }] = useSubmitClaimMutation();
  // Same rationale as submit/open above: both toast their own failure via parseSahApiError rather
  // than feeding the shared error banner, so no `error` is pulled off them - but `reset` still is.
  const [sendClaimToSa, { isLoading: sendLoading, reset: resetSendClaimToSa }] = useSendClaimToSaMutation();
  const [finaliseAndSubmitClaim, { isLoading: finaliseSubmitLoading, reset: resetFinaliseAndSubmit }] =
    useFinaliseAndSubmitClaimMutation();

  const claimQueryArgs = useMemo<{ id: string } | typeof skipToken>(() => {
    if (!id) return skipToken;
    return { id };
  }, [id]);

  const itemsQueryArgs = useMemo<
    ({ id: string; limit?: number; offset?: number } & Record<string, any>) | typeof skipToken
  >(() => {
    if (!id) return skipToken;
    const offset = (debouncedPage - 1) * PAGE_SIZE;
    return { id, limit: PAGE_SIZE, offset, ...searchParams };
  }, [id, debouncedPage, searchParams]);

  const {
    data: claim,
    isLoading: claimLoading,
    isUninitialized: claimUninitialized,
    refetch: refetchClaim,
  } = useGetClaimQuery(claimQueryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const { data: claimItems, isFetching: itemsLoading } = useGetClaimItemsQuery(itemsQueryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const isLoading = claimLoading;

  useEffect(() => {
    dispatch(getServices({ deleted: { or: [true, false] } }));
  }, [dispatch]);

  useEffect(() => {
    if (claim) {
      setTitle(`Claim - ${claim.reference}`);
    } else {
      setTitle('Claim Details');
    }
  }, [claim]);

  useEffect(() => {
    // Debounce to prevent rapid API calls when changing
    if (pageDebounceTimer.current) {
      clearTimeout(pageDebounceTimer.current);
    }
    pageDebounceTimer.current = setTimeout(() => {
      setDebouncedPage(currentPage);
    }, 150);

    return () => {
      if (pageDebounceTimer.current) {
        clearTimeout(pageDebounceTimer.current);
      }
    };
  }, [currentPage]);

  useEffect(() => {
    return () => {
      resetDeleteClaim();
      resetFinaliseClaim();
      resetUnfinaliseClaim();
      resetSubmitClaim();
      resetSendClaimToSa();
      resetFinaliseAndSubmit();
    };
  }, []);

  // Poll for validation completion while is_validating is true
  const prevIsValidatingRef = useRef(claim?.is_validating);
  useEffect(() => {
    // Detect transition from validating → done and refresh claim items
    if (prevIsValidatingRef.current && !claim?.is_validating && id) {
      dispatch(claimsApi.util.invalidateTags([{ type: 'claim-items', id: `CLAIM_${id}` }]));
    }
    prevIsValidatingRef.current = claim?.is_validating;

    if (!claim?.is_validating) return;

    const interval = setInterval(() => {
      refetchClaim();
    }, VALIDATION_POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [claim?.is_validating, refetchClaim, dispatch, id]);

  const onDelete = async () => {
    if (!id) {
      setDeleteModalOpen(false);
      return;
    }

    try {
      const { data: payload } = await deleteClaim({ id });
      if (payload?.success) {
        navigate('/support-at-home/claims');
      }
    } finally {
      setDeleteModalOpen(false);
    }
  };

  // Doubles as the retry handler for "Send remaining items to Services Australia" (TAV-2590):
  // re-POSTing /finalise resumes an in-progress Services Australia push idempotently server-side
  // (guarded by the already-set sa_invoice_id / sa_invoice_item_id columns), so no separate
  // mutation or endpoint is needed for retry.
  const onFinalise = async () => {
    if (!id) return;
    const { data: payload } = await finaliseClaim({ id });
    // finaliseClaim's transformResponse (unwrap in axiosWrapper.ts) strips the outer
    // `{ success, message }` envelope once the response has a `data` key, which it now always
    // does for finalise (TAV-2590 added `data: { finalised, api }`). So `payload` here is that
    // inner `{ finalised, api }` object, not the full envelope - `payload.finalised` is always
    // true on a 200, so it replaces the old `payload?.success` check.
    if (!payload?.finalised) return;
    const { api } = payload;
    // `skipped` BEFORE the `!attempted` check, because the skipped shape is `{ attempted: false,
    // skipped: true, reason }` and would otherwise fall into the generic branch below. It means the
    // provider IS connected to PRODA but the connection carries no `serviceProviderId`, so nothing
    // could be pushed - a misconfiguration, not a clean no-op. `sa_invoice_id` is never set in this
    // case, so ClaimApiPushSummary renders nothing and this toast is the ONLY place the state
    // surfaces; a plain 'Claim finalised' here is indistinguishable from a completed push and would
    // leave a broken connection to be discovered from the Services Australia portal much later.
    if (api?.skipped) {
      const reason = api.reason ? ` - ${api.reason}` : '';
      showToast('info', `Claim finalised. Nothing was sent to Services Australia${reason}.`);
      return;
    }
    if (!api?.attempted) {
      showToast('success', 'Claim finalised');
      return;
    }
    if (api.failed) {
      // parseSahApiError strips the status code / request path / raw JSON envelope off the vendor
      // error - a toast is the least appropriate place for the full string, which stays intact in
      // the response and on the claim item for diagnosis.
      const reason = parseSahApiError(api.reason) || 'unknown error';
      showToast('danger', `Claim finalised. Services Australia push failed - ${reason}.`);
      return;
    }
    if ((api.items_pending ?? 0) > 0) {
      showToast(
        'danger',
        `Claim finalised. ${api.items_pushed ?? 0} of ${api.items_total ?? 0} items sent to Services Australia - use "Send remaining items" to continue.`,
      );
      return;
    }
    showToast('success', `Claim finalised. ${api.items_pushed ?? 0} items sent to Services Australia.`);
  };

  /**
   * Human summary of one reconcile result, shared by Send and Finalise & Submit.
   *
   * Reports what actually CHANGED at Services Australia rather than a flat "sent". On a re-send the
   * interesting number is almost always items_updated or items_deleted, and an all-zero result has to
   * read as "nothing to do" — otherwise a correct no-op is indistinguishable from a button that
   * silently failed, which is the single most confusing outcome for an action whose whole purpose is
   * "press me again after you edit".
   */
  const describeSync = (api: ClaimApiPushResult) => {
    const parts: string[] = [];
    if (api.items_created) parts.push(`${api.items_created} added`);
    if (api.items_updated) parts.push(`${api.items_updated} updated`);
    if (api.items_replaced) parts.push(`${api.items_replaced} recreated`);
    if (api.items_deleted) parts.push(`${api.items_deleted} removed`);
    return parts.length ? parts.join(', ') : 'no changes needed';
  };

  const onSendToSa = async () => {
    if (!id) return;
    try {
      const api = await sendClaimToSa({ id }).unwrap();
      setLastSync(api);
      // Connected to PRODA but the connection carries no serviceProviderId. Nothing could be sent, so
      // sa_invoice_id is never set and the push summary below renders nothing — this toast is the
      // only place the misconfiguration surfaces. Same reasoning as the finalise handler above.
      if (api?.skipped) {
        showToast('info', `Nothing was sent to Services Australia${api.reason ? ` - ${api.reason}` : ''}.`);
        return;
      }
      if ((api?.items_pending ?? 0) > 0) {
        showToast(
          'danger',
          `${api.items_pushed ?? 0} of ${api.items_total ?? 0} items sent to Services Australia. Press Send remaining items to continue.`,
        );
        return;
      }
      // Warnings carry the things the user has to act on personally — most importantly an item whose
      // care recipient/service/date changed, whose attachment must be uploaded again. The banner
      // itemises them; this only makes sure the toast does not read "all good" when they exist.
      const hasWarnings = (api?.warnings?.length ?? 0) > 0;
      showToast(
        hasWarnings ? 'info' : 'success',
        `Sent to Services Australia - ${describeSync(api)}.${hasWarnings ? ' Some items need attention.' : ''}`,
      );
    } catch (error) {
      showToast(
        'danger',
        parseSahApiError(getErrorMessage(error)) || 'Could not send the claim to Services Australia.',
      );
    }
  };

  const onFinaliseSubmit = async () => {
    if (!id) return;
    try {
      const payload = await finaliseAndSubmitClaim({ id }).unwrap();
      if (payload?.api) setLastSync(payload.api);
      // The pre-lodge reconcile did NOT run. The company is connected but its connection carries no
      // serviceProviderId, so syncClaimToApi returned `{ attempted: false, skipped: true }` before
      // touching Services Australia. The submission itself still succeeded — submitApiInvoice needs
      // only the invoice id — so this is a 200 and not a failure, which is exactly what makes it
      // dangerous: the server reconciles BEFORE lodging precisely so an edit made since the last
      // send is never lodged stale, and there is no undo once Services Australia holds the invoice.
      // A plain success toast here would present a stale lodgement as a clean one.
      //
      // The sibling reconcile outcomes need no branch: `failed` is set by nothing in the api, and a
      // partial push is refused upstream (finalise_submit re-counts unpushed items after the
      // reconcile and answers 400), so both reach the catch below rather than arriving on a 200.
      if (payload?.api?.skipped) {
        const reason = payload.api.reason ? ` - ${payload.api.reason}` : '';
        showToast(
          'danger',
          `Claim finalised and submitted, but it was not re-checked against Services Australia first${reason}. ` +
            'Any edits made since the last send were not included.',
        );
        return;
      }
      // HELD is a 200 and must not be toasted as a failure — Services Australia holds the invoice
      // internally pending their own approval, and the spec states verbatim that no provider action
      // is required. Same treatment as the standalone submit handler below.
      if (isSaInvoiceHeld(payload?.sa_invoice_status)) {
        showToast(
          'info',
          'Claim finalised and submitted. Services Australia is holding the invoice for review - no action is required.',
        );
        return;
      }
      showToast('success', 'Claim finalised and submitted to Services Australia.');
    } catch (error) {
      showToast(
        'danger',
        parseSahApiError(getErrorMessage(error)) || 'Could not finalise and submit the claim.',
      );
    }
  };

  const onUnfinalise = async () => {
    if (!id) return;
    try {
      await unfinaliseClaim({ id }).unwrap();
      // Two different things happened depending on where the claim was, and the message says which:
      // a SUBMITTED claim was re-opened at Services Australia on the way back, a finalised one was
      // purely local. Either way the Services Australia invoice is still linked - saying so is the
      // point, because the old behaviour deleted it and users learned to expect that.
      showToast(
        'success',
        isSubmitted
          ? 'Claim re-opened at Services Australia and returned to draft. The invoice is still linked.'
          : 'Claim returned to draft. The Services Australia invoice is still linked.',
      );
    } catch (error) {
      showToast('danger', parseSahApiError(getErrorMessage(error)) || 'Could not unfinalise the claim.');
    }
  };

  const onSubmit = async () => {
    if (!id) return;
    try {
      const payload = await submitClaim({ id }).unwrap();
      // HELD is a 200: Services Australia holds the invoice internally pending their own approval and
      // the spec states verbatim that no action is required by the provider. It is informational only -
      // it does not block, does not change the TurnPoint status, and must not be toasted as a failure.
      if (isSaInvoiceHeld(payload?.sa_invoice_status)) {
        showToast('info', 'Claim submitted. Services Australia is holding the invoice for review - no action is required.');
        return;
      }
      showToast('success', 'Claim submitted to Services Australia.');
    } catch (error) {
      showToast('danger', parseSahApiError(getErrorMessage(error)) || 'Could not submit the claim.');
    }
  };


  const onGenerateCsv = async () => {
    if (!id) return;
    try {
      await generateSeparate(DOCUMENT_TYPES.SAH_CLAIM.CSV, [id]).unwrap();
      showToast('success', 'CSV generation started');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start CSV generation';
      showToast('danger', message);
    }
  };

  const onDownload = async (updateProgressValue?: (value: string) => void) => {
    if (!id) return null;
    try {
      const data = await download(`${apiSahClaims}/${id}/export-aco`, {}, (progress) => {
        updateProgressValue?.(typeof progress === 'number' ? `${progress}%` : String(progress));
      });
      const reference = claim?.reference || id;
      const sanitizedFilename = reference.replace(/[/\\:*?"<>|]/g, '_').trim();
      return getStreamableZipDownload({ blob: data.data, filename: `${sanitizedFilename}.zip` });
    } catch (err) {
      console.error('Export failed:', err);
      return null;
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRefreshClaim = () => {
    if (!claimUninitialized) {
      refetchClaim();
    }
  };

  const statusTag = claim?.status?.tag;
  const isFinalised = statusTag === CLAIM_STATUS_FINALISED;
  const isSubmitted = isClaimSubmitted(statusTag);
  // TAV-2595. Every state Services Australia has claimed the invoice in, not just `claiming` - see
  // isClaimClaimed. `isClaimClaiming` covered the whole set only while `claiming` was the only member.
  const isClaimed = isClaimClaimed(statusTag);
  // Every existing lock asked "is this claim finalised?" but MEANT "is it past the editable point".
  // A submitted claim is strictly further along, so it must lock at least as hard.
  const isLocked = isClaimLocked(statusTag);
  // TAV-3100. Keyed on the SA invoice status, not the tag: a held claim is still tagged `submitted`.
  const isHeld = isSaInvoiceHeld(claim?.sa_invoice_status);
  const hasStatementItems = claim?.items?.some((item) => item.statement?.id) ?? false;

  const hasApiInvoice = Boolean(claim?.sa_invoice_id);
  // Company-level, from get_one. Splits the primary action into two worlds: a provider with PRODA
  // direct claiming available sends to Services Australia and then lodges, everyone else keeps the
  // unchanged Finalise button. As of TAV-3192 "available" is the `feature-proda-direct` flag AND the
  // company's connection, so a connected provider with the flag off lands in the second world.
  // It cannot be derived from `hasApiInvoice`, because the choice has to be made BEFORE any invoice
  // exists, which is exactly the state "Send to Services Australia" is offered in.
  const saConnected = Boolean(claim?.sa_connected);
  // TAV-3101. Unfinalise is the only exit from `submitted` and there is no way to un-claim at
  // Services Australia, so the route is :Admin and this gates the button the same way.
  const canAdministerClaims = useCanAdministerClaims();
  const outstandingCount =
    (claim?.items_attachment_required ?? 0) +
    (claim?.items_further_info_required ?? 0) +
    (claim?.items_late_submission_required ?? 0);
  const itemsPendingPush = claim?.items_pending_push ?? 0;
  // Mirrors the server gate exactly (company_sah_claims.mjs submit()), from counts get_one already
  // returns - no new request. The server gate remains authoritative; this only avoids a pointless 400.
  const submitBlocked = outstandingCount > 0 || itemsPendingPush > 0;
  const canSubmit = isFinalised && !isSubmitted && hasApiInvoice;

  const deleteErr = deleteError as any;
  const finaliseErr = finaliseError as any;
  // Every mutation that can reach Services Australia is deliberately ABSENT from this list - submit,
  // send, finalise & submit, and now UNFINALISE, which calls the vendor's /open when the claim is
  // submitted. Each handler catches its own rejection and toasts `parseSahApiError(...)`, whereas
  // this banner renders `data.message` verbatim - and on a vendor rejection the API sets that to the
  // complete raw string, JSON envelope included. Listing them here would show the same failure twice
  // in two wordings, the banner's being exactly the raw payload AC #6 says must never reach the
  // provider. One surface per failure: the toast.
  const errors = [deleteErr?.data?.message, finaliseErr?.data?.message].filter(Boolean);

  const getHeaderTitle = () => {
    if (isLoading) {
      return 'Loading...';
    }
    return claim ? claim.reference : 'Not Found';
  };

  /**
   * The Send / Sync action. It lives in the push summary BANNER, not the page toolbar - it acts on
   * the Services Australia relationship the banner describes, and in the pre-invoice state the prompt
   * ("No invoice created…") and the button that answers it should be the same thing the eye lands on.
   *
   * Built here rather than inside the banner because its enabled state and label depend on claim facts
   * this component already derives; the banner would otherwise have to re-derive them and keep them in
   * step.
   *
   * Disabled rather than hidden once the claim is locked. finalise() is a purely local transition
   * now, so a finalised claim was deliberately locked WITHOUT involving Services Australia - opening
   * an invoice against a claim nobody can edit any more is the exact trap this flow exists to remove.
   * The route enforces the same rule. Showing it disabled with the remedy in its accessible name
   * ("unfinalise the claim first") beats hiding it: a button that vanishes reads as "not available to
   * you", a disabled one reads as "here is what to do".
   */
  const renderSendButton = () => {
    const blockedByErrors = (claim?.items_with_errors ?? 0) > 0;
    let reason = '';
    if (isLocked) reason = ' - unfinalise the claim first';
    else if (hasStatementItems) reason = ' - blocked, the claim has items in a SaH statement';
    else if (blockedByErrors) reason = ' - blocked, the claim has items with validation errors';

    // Three labels for one action, because the call to action genuinely differs. An interrupted push
    // gets the most specific wording; once everything has landed, the steady-state label is "Sync"
    // rather than "Re-send" because the action reads Services Australia's state back as well as
    // pushing local changes out - "Re-send" named only the outbound half (TAV-3234).
    let label = 'Send to Services Australia';
    if (hasApiInvoice) {
      label = itemsPendingPush > 0 ? 'Send remaining items to Services Australia' : 'Sync with Services Australia';
    }

    return (
      <Button
        size="sm"
        className="ps-4 pe-4 text-nowrap"
        color="success"
        // Otherwise deliberately the SAME gate as Finalise: the attachment / further-information /
        // late-submission flags this action's own result raises are not knowable until Services
        // Australia has seen the items, so there is nothing else to check beforehand.
        disabled={Boolean(reason)}
        loading={sendLoading}
        // Button's `loading` prop replaces children with a spinner, which loses the accessible name,
        // and a disabled <button> never fires a title tooltip in Chrome - so the reason rides here.
        aria-label={`${label}${reason}`}
        onClick={onSendToSa}
        dataCy="sah-claim-send-to-sa-button"
      >
        {label}
      </Button>
    );
  };

  /**
   * Plain, local-only Finalise. Still offered to a connected provider BEFORE the invoice exists, so
   * they can lock a claim manually without involving Services Australia at all - the flat-file / CSV
   * path, and the state the disabled Send button above then explains.
   *
   * Stays `success` green even when it sits beside Send. Both are legitimate primary actions at that
   * point rather than a recommended path and a fallback, so demoting one would misrepresent the
   * choice.
   */
  const renderFinaliseButton = () => (
    // TAV-2590: this disabled condition is intentionally NOT extended to consider
    // attachment/further-information-required counts. Those flags are only knowable AFTER items have
    // been sent to Services Australia, so there is no state to check beforehand - and blocking
    // finalise for an API provider would ALSO block their flat file CSV download, because the
    // "Generate CSV" / "Export ACO Upload" buttons are themselves gated on the claim being finalised
    // (`disabled={!isFinalised}`). The block on an incomplete invoice lives on Finalise & Submit.
    <Button
      size="sm"
      className="ps-4 pe-4"
      color="success"
      disabled={(claim?.items_with_errors ?? 0) > 0 || isLocked || hasStatementItems}
      loading={finaliseLoading}
      onClick={onFinalise}
      dataCy="sah-claim-finalise-button"
    >
      Finalise
    </Button>
  );

  const renderClaimStateAction = () => {
    // Explicit branch, not a fall-through. There is deliberately no action here: Services Australia
    // has no un-claim operation, so a claimed claim has no state transition a user can drive. The
    // state is shown by the claim status line in the push summary.
    //
    // TAV-2595 widened this from `claiming` alone to every CLAIMED tag, mirroring the API's own
    // widening from isClaimClaimingTag to isClaimClaimedTag (which is what /unfinalise refuses on).
    // Leaving it single-tag would NOT have re-created TAV-2593's disabled-Finalise trap - the four new
    // tags are in isClaimLocked, so they reach the branch below and get an ENABLED Unfinalise button
    // instead, which the API answers with a 400. A dead button that looks alive is the worse of the two.
    if (isClaimed) return null;
    // ONE button for both locked states. Re-open is gone: unfinalising a SUBMITTED claim re-opens its
    // invoice at Services Australia AND returns the claim to draft in a single action, which is what
    // the user is actually trying to do - the two-step "re-open, then unfinalise" dance existed only
    // because unfinalise used to be destructive.
    //
    // TAV-3101. NOT offered, rather than offered-and-refused: unfinalise is the only exit from
    // `submitted` and it is irreversible in the sense that matters here, so the route is :Admin and
    // this branch returns null for everyone else. Returning null EXPLICITLY rather than falling
    // through is load-bearing - the branches below end at renderFinaliseButton(), which would put a
    // Finalise button on a locked claim.
    //
    // TAV-3100. Disabled, not hidden, while Services Australia is holding the invoice: the API refuses
    // unfinalise on a held claim (SA cannot re-open it), so the button explains why instead of vanishing,
    // as renderSendButton does. Keyed on `isHeld`, never the tag, so a plain submitted claim stays enabled.
    if (isLocked || hasStatementItems) {
      if (!canAdministerClaims) return null;
      let unfinaliseLabel = isSubmitted ? 'Unfinalise claim and re-open it at Services Australia' : 'Unfinalise claim';
      if (isHeld) {
        unfinaliseLabel =
          'Unfinalise claim - blocked, Services Australia is holding the invoice pending their approval and no action is required';
      }
      return (
        <Button
          size="sm"
          className="ps-4 pe-4"
          color="danger"
          disabled={isHeld}
          loading={unfinaliseLoading}
          // `loading` swaps the children for a spinner, losing the accessible name, and a disabled
          // <button> never fires a title tooltip in Chrome - so the held reason rides here too.
          aria-label={unfinaliseLabel}
          onClick={onUnfinalise}
          dataCy="sah-claim-unfinalise-button"
        >
          Unfinalise
        </Button>
      );
    }
    // Once the invoice exists, Finalise BECOMES Finalise & Submit - there is no separate local
    // finalise step left for a connected provider, and finalise() itself refuses a claim that holds an
    // invoice. `!isLocked` is redundant after the branch above returns for every locked tag, but kept
    // as an explicit statement that this is the DRAFT action: it is the condition a future edit is
    // most likely to get wrong.
    if (!isLocked && saConnected && hasApiInvoice) {
      return (
        <Button
          size="sm"
          className="ps-4 pe-4"
          color="success"
          // Unlike Finalise, this one IS blocked by Services Australia's outstanding requirements -
          // it lodges the invoice, and `submitBlocked` mirrors the server gate exactly (from counts
          // get_one already returns, so no extra request). The server stays authoritative; this only
          // avoids a pointless 400. Blocking finalisation here costs the provider nothing: plain
          // Finalise remains available right up until they choose to send.
          disabled={submitBlocked || hasStatementItems}
          loading={finaliseSubmitLoading}
          // Carries the blocked reason because a disabled <button> never fires a title tooltip in
          // Chrome; the itemised reasons live in the push summary banner below.
          aria-label={
            submitBlocked
              ? `Finalise and submit to Services Australia - blocked, ${outstandingCount + itemsPendingPush} item(s) still need attention`
              : 'Finalise and submit to Services Australia'
          }
          onClick={onFinaliseSubmit}
          dataCy="sah-claim-finalise-submit-button"
        >
          Finalise &amp; Submit
        </Button>
      );
    }
    // Plain, local-only Finalise. Send is no longer a sibling here - it lives in the banner - but both
    // remain real choices at this point: send the invoice to Services Australia now and lodge later, or
    // finalise locally without involving Services Australia at all.
    return renderFinaliseButton();
  };

  return (
    // TAV-3332 (finding 30). See .sah-claim-detail in _claims.scss for why this class is here.
    <div className="p-2 sah-claim-detail">
      <Modal
        isOpen={deleteModalOpen}
        setIsOpen={setDeleteModalOpen}
        headerClassName="bg-warning"
        header="Warning"
        includeFooter={true}
        footer={
          <div className="d-flex gap-2">
            <Button size="sm" className="ps-4 pe-4" color="danger" loading={isDeleting} onClick={onDelete}>
              Delete
            </Button>
            <Button
              size="sm"
              className="ps-4 pe-4"
              data-cy="sah-claim-delete-cancel-button"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div className="mb-4">Are you sure you want to delete claim?</div>
        <div>This action will fail if claim has been finalised.</div>
        <div className="fw-bold mt-4">This is an irreversible action</div>
      </Modal>

      <ContainerHeader dataCy="claim-header" className="info">
        <span>Claim Details - {getHeaderTitle()}</span>
      </ContainerHeader>

      <Toolbar
        id="claim-details-toolbar"
        leftCluster={
          <ClickableIcon
            icon={faArrowLeft}
            id="claim-back-button"
            onClick={() => navigate('/support-at-home/claims')}
            toolTip="Back to Claims"
          />
        }
        rightCluster={
          <>
            <ClickableIcon
              id="claim-items-search-button"
              icon={faMagnifyingGlass}
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <ClickableIcon
              id="sah-claim-delete-icon"
              icon={faTrash}
              className="mx-2"
              color="danger"
              disabled={isLocked || hasStatementItems}
              showTooltipWhenDisabled={hasStatementItems}
              onClick={() => setDeleteModalOpen(true)}
              toolTip={hasStatementItems ? 'Cannot delete - claim has items included in statements' : 'Delete claim'}
            />
            <DownloadButton
              caption="Export ACO Upload"
              classNames="ps-4 pe-4"
              handleDownload={onDownload}
              disabled={!isLocked}
              size="sm"
              color="secondary"
            />
            <Button size="sm" className="ps-4 pe-4" color="secondary" loading={isGenerating} disabled={isGenerating} onClick={onGenerateCsv}>
              Generate CSV
            </Button>
            {renderClaimStateAction()}
            {canSubmit && (
              <Button
                size="sm"
                className="ps-4 pe-4"
                color="success"
                disabled={submitBlocked}
                loading={submitLoading}
                // Button's `loading` prop replaces children with a spinner, losing the accessible name. It also
                // carries the blocked reason, because a disabled <button> never fires a title tooltip in Chrome -
                // the itemised reasons themselves live in the push summary banner below.
                aria-label={
                  submitBlocked
                    ? `Submit to Services Australia - blocked, ${outstandingCount + itemsPendingPush} item(s) still need attention`
                    : 'Submit to Services Australia'
                }
                onClick={onSubmit}
                dataCy="sah-claim-submit-button"
              >
                Submit to Services Australia
              </Button>
            )}
          </>
        }
      />

      <ErrorResponse message={errors.join(',')} dataCy="claim-errorResponse" />
      {/*
        TAV-2590: gated on claim.sa_invoice_id, not PRODA connection state - see the comment
        inside ClaimApiPushSummary for why. Renders null for non-API providers.
      */}
      {claim && (
        <ClaimApiPushSummary
          claim={claim}
          // Sync, not re-finalise. finalise() now REFUSES any claim that already holds an SA
          // invoice, so the old "re-POST /finalise to resume the push" retry no longer exists -
          // /send-to-sa owns it, and admits `finalised` claims precisely so claims pushed under the
          // previous flow keep a working retry.
          // The Send / Sync action now lives in the banner rather than the page toolbar.
          sendButton={saConnected ? renderSendButton() : null}
          attachmentFilterActive={searchParams.show_attachment_required === 'Yes'}
          onToggleAttachmentFilter={() => toggleItemsFilter('show_attachment_required')}
          // TAV-2592: same drill-down pattern as onToggleAttachmentFilter above, targeting
          // show_late_submission instead of show_attachment_required.
          lateSubmissionFilterActive={searchParams.show_late_submission === 'Yes'}
          onToggleLateSubmissionFilter={() => toggleItemsFilter('show_late_submission')}
          submissionBlocked={canSubmit && submitBlocked}
          syncResult={lastSync}
        />
      )}
      <SearchBar isOpen={searchBarOpen} fields={claimItemsSearchFields} onSubmit={search} />
      {isLoading ? (
        <div className="d-flex justify-content-center p-5">
          <Spinner loading />
        </div>
      ) : (
        <>
          {claim ? (
            <div className="p-2">
              <ClaimDetailsEdit
                claim={claim}
                claimItems={claimItems}
                itemsLoading={itemsLoading}
                currentPage={currentPage}
                pageSize={PAGE_SIZE}
                onPageChange={handlePageChange}
                onRefreshClaim={handleRefreshClaim}
              />
            </div>
          ) : (
            <PageNotFound content={getMissingContent('claim')} />
          )}
        </>
      )}
    </div>
  );
};

export default Claim;
